import { useLazyQuery } from '@apollo/client';
import { ErrorRetry } from '@bechtel/client-common';
import { GET_EVENT_NOTIFICATION_STATS } from '@bechtel/client-gql';
import { Box, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { EventNotificationInput, EventNotificationStat } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { EntityStyled } from './styled';
import { EntityProps } from './types';

export const EntityStats: React.FC<EntityProps> = ({ onEntitySelected, selectedEventNotificationFilter }) => {
  console.log('Selected Event Notification Filter:', selectedEventNotificationFilter);
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set([]));
  const [getEventNotificationStats, { data, error, loading, called, refetch }] = useLazyQuery<
    { eventNotificationStats: EventNotificationStat[] },
    { input: EventNotificationInput }
  >(GET_EVENT_NOTIFICATION_STATS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    getEventNotificationStats({
      variables: {
        input: {
          complexFilter: selectedEventNotificationFilter,
        },
      },
    });
  }, [selectedEventNotificationFilter]);

  const entityList = get(data, 'eventNotificationStats', []);

  const onClick = (name: string) => {
    if (selectedEntities.has(name)) {
      setSelectedEntities((prev) => {
        const newEntities = new Set(prev);
        newEntities.delete(name);
        return newEntities;
      });
    } else {
      setSelectedEntities((prev) => new Set(prev).add(name));
    }
  };

  useEffect(() => {
    onEntitySelected(selectedEntities);
  }, [selectedEntities]);

  return (
    <>
      {loading && (
        <Box data-testid="entity-stats-loading" justifyContent={{ sm: 'center' }} css="width:100%" mt={{ sm: '400' }}>
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
          data-testid="user-list-retry"
        >
          <ErrorRetry refetch={refetch}>
            <Trans>
              There was a problem loading the stats for the events. Please click the button below to try again.
            </Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading &&
        !error &&
        entityList.map((entity) => {
          const active = selectedEntities.has(entity.entityType);

          return (
            <EntityStyled
              data-testid={`entity-stat-${entity.id}`}
              key={entity.id}
              active={active}
              onClick={() => onClick(entity.entityType)}
            >
              <Text weight="600" family="graphikCompact" size="600" color={active ? 'white' : 'grey.1000'}>
                {entity.stats}
              </Text>
              <Text family="graphikCompact" size="300" color={active ? 'white' : 'grey.600'}>
                {entity.entityType}
              </Text>
            </EntityStyled>
          );
        })}
    </>
  );
};
